import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAddress } from '../contexts/AddressContext';
import { formatPrice as formatPriceWithCurrency, SupportedCurrency, getCurrencyFromLanguage } from '../utils/currencyUtils';
import { getCurrentLanguage } from '../config/languageConfig';
import useTranslation from '../hooks/useTranslation';
import { useAddressAffordance } from '../contexts/AddressAffordanceContext';
import { getPromotionUserId } from '../utils/promotionUserIdUtils';
import { 
  calculateOrder, 
  calculateOrderGift, 
  transformCartToCalculateRequest, 
  transformCartToCalculateGiftRequest,
  CalculateOrderResponse 
} from '../api/cartService';

interface CartBottomSheetProps {
  minOrderAmount: number | null;
  onCheckout: () => void;
  restaurantId: string;
  currency?: SupportedCurrency;
}

const CartBottomSheet: React.FC<CartBottomSheetProps> = ({ 
  minOrderAmount, 
  onCheckout, 
  restaurantId,
  currency
}) => {
  const { 
    cart, 
    getTotalItems, 
    isDeliveryAvailable, 
    getAmountToMinOrder,
    switchMessage,
    clearSwitchMessage,
    setCurrentRestaurantId,
    currentRestaurantId
  } = useCart();
  const { address } = useAddress();
  const { t } = useTranslation();
  const { checkAddressAndExecute } = useAddressAffordance();
  const [isVisible, setIsVisible] = useState(false);
  const [showSwitchMessage, setShowSwitchMessage] = useState(false);
  const [orderCalculation, setOrderCalculation] = useState<CalculateOrderResponse | null>(null);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const navigate = useNavigate();
  
  // 현재 언어에 맞는 통화 설정
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const effectiveCurrency = useMemo(() => {
    return getCurrencyFromLanguage(currentLang);
  }, [currentLang]);
  
  // 언어 변경 감지를 위한 이벤트 리스너 추가
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };
    
    // 언어 변경 이벤트 리스너 등록
    window.addEventListener('language-changed', handleLanguageChange);
    
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('language-changed', handleLanguageChange);
    };
  }, []);
  
  // 기프트샵 여부 확인
  const isGiftShop = useMemo(() => {
    return restaurantId === 'gift-shop-1';
  }, [restaurantId]);

  // 주문 금액 계산 API 호출
  useEffect(() => {
    const calculateOrderAmount = async () => {
      if (!cart || !cart.items.length || !address?.addressId || cart.restaurantId !== restaurantId) {
        setOrderCalculation(null);
        return;
      }

      try {
        setIsCalculating(true);
        const promotionUserId = getPromotionUserId();
        
        const calculation = isGiftShop 
          ? await calculateOrderGift(transformCartToCalculateGiftRequest(
              cart, 
              address.addressId, 
              currentLang.toUpperCase()
            ), promotionUserId)
          : await calculateOrder(transformCartToCalculateRequest(
              cart, 
              address.addressId, 
              currentLang.toUpperCase()
            ), promotionUserId);
        
        setOrderCalculation(calculation);
        
      } catch (error) {
        console.error('[CartBottomSheet] 주문 금액 계산 실패:', error);
        setOrderCalculation(null);
      } finally {
        setIsCalculating(false);
      }
    };

    calculateOrderAmount();
  }, [cart, address?.addressId, restaurantId, currentLang, isGiftShop]);
  
  // 현재 레스토랑 ID 업데이트
  useEffect(() => {
    // 이전 값과 다를 때만 업데이트
    if (currentRestaurantId !== restaurantId) {
      setCurrentRestaurantId(restaurantId);
    }
  }, [restaurantId, setCurrentRestaurantId, currentRestaurantId]);
  
  // 카트 상태가 변경될 때마다 애니메이션 효과를 위한 상태 업데이트
  useEffect(() => {
    if (cart && cart.items.length > 0 && cart.restaurantId === restaurantId) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [cart, restaurantId]);
  
  // 전환 메시지 표시 처리
  useEffect(() => {
    if (switchMessage) {
      setShowSwitchMessage(true);
      const timer = setTimeout(() => {
        setShowSwitchMessage(false);
        clearSwitchMessage();
      }, 3000); // 3초 후 자동으로 닫힘
      return () => clearTimeout(timer);
    }
  }, [switchMessage, clearSwitchMessage]);
  
  // API 응답에서 subtotal만 사용 (프로모션 무시)
  const totalPrice = useMemo(() => {
    if (!orderCalculation) {
      // API 응답이 없을 때는 기존 로직 사용 (fallback)
      return cart?.items.reduce((total, item) => total + (item.price * item.quantity), 0) || 0;
    }
    
    // 프로모션 상관없이 subtotal만 사용
    switch(effectiveCurrency) {
      case 'USD': return orderCalculation.subtotalUsd;
      case 'JPY': return orderCalculation.subtotalJpy;
      case 'CNY': return orderCalculation.subtotalCny;
      case 'TWD': return orderCalculation.subtotalTwd;
      default: return orderCalculation.subtotalKrw;
    }
  }, [orderCalculation, effectiveCurrency, cart]);
  
  // 카트가 비어있거나, 메시지가 없거나, 현재 레스토랑과 카트의 레스토랑이 다르면 렌더링하지 않음
  if (
    ((!cart || cart.items.length === 0) && !showSwitchMessage) || 
    (cart && cart.restaurantId !== restaurantId)
  ) {
    return null;
  }
  
  const totalItems = getTotalItems();

  // 최소 주문 충족 여부: 서버의 isMinOrderAmountMet(상품 소계 기준) 우선, 없으면 클라이언트 계산 fallback
  const deliveryAvailable = (orderCalculation && typeof orderCalculation.isMinOrderAmountMet === 'boolean')
    ? orderCalculation.isMinOrderAmountMet
    : isDeliveryAvailable(minOrderAmount);

  // 표시용 부족분: 실효 최소금액(응답 우선) - 상품 소계
  const effectiveMinOrderAmount = (orderCalculation && typeof orderCalculation.minOrderAmount === 'number')
    ? orderCalculation.minOrderAmount
    : minOrderAmount;
  const amountToMinOrder = effectiveMinOrderAmount != null
    ? Math.max(0, effectiveMinOrderAmount - totalPrice)
    : getAmountToMinOrder(minOrderAmount);
  
  // 가격 포맷팅 (선택된 통화 사용)
  const formatPrice = (price: number) => {
    return formatPriceWithCurrency(price, effectiveCurrency);
  };
  
  // 장바구니 페이지로 이동 (주소 체크)
  const handleCartClick = (e: React.MouseEvent) => {
    e.preventDefault();
    checkAddressAndExecute(() => {
      // 주소가 설정된 경우에만 장바구니 페이지로 이동
      navigate('/cart');
    });
  };
  
  return (
    <div className="fixed bottom-[60px] left-1/2 transform -translate-x-1/2 z-10 max-w-mobile w-full">
      <div 
        className={`w-full bg-white rounded-t-xl transition-all duration-300 ease-in-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        style={{ 
          boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06)'
        }}
      >
        {/* 전환 메시지 알림 */}
        {showSwitchMessage && switchMessage && (
          <div className="bg-yellow-100 text-yellow-700 px-4 py-2 border-b border-yellow-200">
            <div className="flex justify-between items-center">
              <p className="text-sm font-medium">{switchMessage}</p>
              <button 
                onClick={() => {
                  setShowSwitchMessage(false);
                  clearSwitchMessage();
                }}
                className="text-yellow-500 hover:text-yellow-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}
        
        {/* 카트 콘텐츠 - 현재 레스토랑과 카트의 레스토랑이 같을 때만 표시 */}
        {cart && cart.items.length > 0 && cart.restaurantId === restaurantId && (
          <div className="py-2.5 px-4 pb-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-shrink-0 w-2/5">
                {deliveryAvailable ? (
                  <span className="text-base font-medium text-gray-800 whitespace-nowrap">{t('cart.deliveryAvailable')}</span>
                ) : (
                  <span className="text-base font-medium text-red-500 whitespace-nowrap">{t('cart.addMoreAmount', { amount: formatPrice(amountToMinOrder) })}</span>
                )}
              </div>
              
              <div className="flex-grow flex justify-end w-3/5">
                <button
                  onClick={handleCartClick}
                  className={`max-w-[200px] ml-auto text-white py-3 px-3 rounded-lg font-semibold flex items-center justify-center z-50 ${
                    deliveryAvailable ? 'bg-red-500 hover:bg-red-600 active:bg-red-700' : 'bg-gray-300 opacity-70'
                  }`}
                  style={{ 
                    position: 'relative', 
                    pointerEvents: deliveryAvailable ? 'auto' : 'none'
                  }}
                  disabled={!deliveryAvailable || isCalculating}
                >
                  <div className={`rounded-full w-5 h-5 flex items-center justify-center mr-2 ${
                    deliveryAvailable ? 'bg-white text-red-500' : 'bg-white text-gray-400'
                  }`}>
                    <span className="text-sm font-bold">{totalItems}</span>
                  </div>
                  <span className="text-lg font-semibold text-white">
                    {isCalculating ? '...' : formatPrice(totalPrice)}
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartBottomSheet; 